import React, { useCallback, useEffect, useRef, useState } from 'react';
import Markdown from './markdown';
import { streamSse } from './sse';

// <PlunkChat> — host-agnostic AI chat component (AI_CHAT_UX.md §2).
// No Studio- or App-specific imports; the host supplies apiBase, an auth
// token supplier, and starter suggestions. Conversations persist
// server-side (ChatService) — this component holds only conversationId
// and renders what the backend returns (UX review Q5-1: no client-side
// conversation cache, ever).
//
// SSE contract (ChatService README):
//   token {text} · tool {name,status,label} · message {conversationId,message} · error {message,kind}

const fmtDate = d => {
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const now = new Date();
  const days = Math.floor((now - dt) / 86400000);
  if (days === 0 && dt.getDate() === now.getDate()) return 'Today';
  if (days < 2) return 'Yesterday';
  return dt.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric'
  });
};
function ToolChips({
  tools,
  live
}) {
  // While streaming: one chip per tool, pulsing until done. After the
  // answer completes: collapsed one-line summary, expandable (UX review Q3).
  const [expanded, setExpanded] = useState(false);
  if (!tools || tools.length === 0) return null;
  if (!live && !expanded) {
    return /*#__PURE__*/React.createElement("button", {
      className: "pchat-chipsummary",
      onClick: () => setExpanded(true),
      title: "Show what was checked"
    }, "\u2713 ", tools.length, " check", tools.length === 1 ? '' : 's', " \u2014 ", tools.map(t => t.label || t.name).join(' · '));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "pchat-chips"
  }, tools.map((t, i) => /*#__PURE__*/React.createElement("span", {
    key: i,
    className: `pchat-chip ${t.done ? 'pchat-chip--done' : 'pchat-chip--active'}`
  }, /*#__PURE__*/React.createElement("span", {
    className: "pchat-chipdot"
  }, t.done ? '✓' : ''), t.label || t.name)), !live && /*#__PURE__*/React.createElement("button", {
    className: "pchat-chipsummary",
    onClick: () => setExpanded(false)
  }, "collapse"));
}
function AssistantBubble({
  msg
}) {
  const empty = !msg.content && !msg.streaming;
  return /*#__PURE__*/React.createElement("div", {
    className: "pchat-row"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pchat-bubble pchat-bubble--assistant"
  }, /*#__PURE__*/React.createElement(ToolChips, {
    tools: msg.tools,
    live: !!msg.streaming
  }), msg.streaming && !msg.content && (!msg.tools || msg.tools.every(t => t.done)) && msg.slow && /*#__PURE__*/React.createElement("span", {
    className: "pchat-shimmer"
  }, "Thinking\u2026"), msg.content && /*#__PURE__*/React.createElement(Markdown, {
    text: msg.content
  }), msg.streaming && msg.content && /*#__PURE__*/React.createElement("span", {
    className: "pchat-caret"
  }), empty && msg.tools?.length > 0 && /*#__PURE__*/React.createElement("span", {
    className: "pchat-shimmer",
    style: {
      animation: 'none'
    }
  }, "No answer text came back \u2014 ask again."), msg.status === 'stopped' && /*#__PURE__*/React.createElement("div", {
    className: "pchat-stopped"
  }, "stopped")));
}
export default function PlunkChat({
  apiBase,
  auth,
  // () => Promise<bearer token>
  extraHeaders,
  // optional static headers (e.g. x-plunk-app)
  conversationId: initialConversationId = null,
  onConversationChange,
  placeholder = 'Ask about your data…',
  suggestions = [],
  welcomeTitle = 'Ask me anything about your data.',
  welcomeSub = null,
  theme = 'studio',
  context = null,
  // reserved (UX review Q4) — v1 hosts pass null
  primeText = null // §284 — {text, ts}: host-primed composer text (onboarding starters); fills the input, NEVER auto-sends
}) {
  const [conversationId, setConversationId] = useState(initialConversationId);
  const [messages, setMessages] = useState(null); // null = loading history
  const [view, setView] = useState('chat'); // chat | list
  const [convos, setConvos] = useState(null);
  const [input, setInput] = useState('');
  useEffect(() => {
    if (primeText && primeText.text) {
      setInput(primeText.text);
      setView('chat');
      setTimeout(() => {
        try {
          document.querySelector('.pchat-input')?.focus();
        } catch (e) {/* noop */}
      }, 50);
    }
  }, [primeText]); // eslint-disable-line react-hooks/exhaustive-deps
  const [streaming, setStreaming] = useState(false);
  const [fatal, setFatal] = useState(null); // {message, retry}
  const abortRef = useRef(null);
  const threadRef = useRef(null);
  const inputRef = useRef(null);
  const atBottomRef = useRef(true);
  const [showJump, setShowJump] = useState(false);
  const slowTimer = useRef(null);
  const skipReloadRef = useRef(false); // our own terminal event set the id — state already final
  // §53b: out-of-credits pause — a calm notice (same visual family as a
  // paused batch job), composer disabled until the user resumes.
  const [paused, setPaused] = useState(null);
  const authHeaders = useCallback(async () => {
    const token = await auth();
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(extraHeaders || {})
    };
  }, [auth, extraHeaders]);
  const api = useCallback(async (path, opts = {}) => {
    // Per-request token via the supplier (UX review Q5-4). One silent
    // retry through the supplier on auth failure before surfacing.
    const run = async () => {
      const res = await fetch(`${apiBase}${path}`, {
        ...opts,
        headers: await authHeaders()
      });
      if (res.status === 401 || res.status === 403) {
        const e = new Error('auth');
        e.kind = 'auth';
        throw e;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.status === 204 ? null : res.json();
    };
    try {
      return await run();
    } catch (e) {
      if (e.kind === 'auth') return run();
      throw e;
    }
  }, [apiBase, authHeaders]);

  // Load history when a conversation is selected (refresh-safe by
  // construction — the server is the source of truth).
  useEffect(() => {
    let dead = false;
    if (!conversationId) {
      setMessages([]);
      return undefined;
    }
    if (skipReloadRef.current) {
      skipReloadRef.current = false;
      return undefined;
    }
    setMessages(null);
    api(`/v1/chat/conversations/${conversationId}`).then(d => {
      if (dead) return;
      setMessages((d.messages || []).map(m => ({
        id: m.id,
        role: m.role,
        content: m.content,
        status: m.status,
        tools: (m.toolActivity || []).map(t => ({
          ...t,
          done: true
        }))
      })));
    }).catch(() => {
      if (dead) return;
      // Conversation gone (deleted / 90-day sweep) — start fresh.
      setConversationId(null);
      onConversationChange?.(null);
      setMessages([]);
    });
    return () => {
      dead = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Auto-scroll follows the stream only while at the bottom (§3).
  const scrollToBottom = () => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };
  useEffect(() => {
    if (atBottomRef.current) {
      scrollToBottom();
      setShowJump(false);
    } else if (streaming) setShowJump(true);
  }, [messages, streaming]);
  const onThreadScroll = () => {
    const el = threadRef.current;
    if (!el) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    if (atBottomRef.current) setShowJump(false);
  };
  const send = useCallback(async text => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput('');
    setFatal(null);
    setStreaming(true);
    atBottomRef.current = true;
    const pendingId = `pending-${Date.now()}`;
    setMessages(prev => [...(prev || []), {
      id: `u-${Date.now()}`,
      role: 'user',
      content: message,
      status: 'complete'
    }, {
      id: pendingId,
      role: 'assistant',
      content: '',
      tools: [],
      streaming: true,
      slow: false
    }]);
    // >4s with nothing rendered → "thinking" shimmer (§3).
    slowTimer.current = setTimeout(() => {
      setMessages(prev => prev.map(m => m.id === pendingId ? {
        ...m,
        slow: true
      } : m));
    }, 4000);
    const controller = new AbortController();
    abortRef.current = controller;
    let received = false; // any SSE event seen — guards the auth retry
    const patch = fn => setMessages(prev => prev ? prev.map(m => m.id === pendingId ? fn(m) : m) : prev);
    const path = conversationId ? `/v1/chat/conversations/${conversationId}/messages` : '/v1/chat/conversations/messages';
    const attempt = async () => streamSse(`${apiBase}${path}`, {
      headers: await authHeaders(),
      body: JSON.stringify({
        message,
        context
      }),
      signal: controller.signal,
      onEvent: (event, data) => {
        received = true;
        if (event === 'token') {
          patch(m => ({
            ...m,
            content: m.content + (data.text || ''),
            slow: false
          }));
        } else if (event === 'tool') {
          patch(m => {
            const tools = [...(m.tools || [])];
            if (data.status === 'start') tools.push({
              name: data.name,
              label: data.label,
              done: false
            });else {
              const open = tools.findLast ? tools.findLast(t => !t.done) : [...tools].reverse().find(t => !t.done);
              if (open) open.done = true;
            }
            return {
              ...m,
              tools,
              slow: false
            };
          });
        } else if (event === 'message') {
          const final = data.message || {};
          patch(m => ({
            ...m,
            id: final.id || m.id,
            content: final.content ?? m.content,
            status: final.status,
            tools: (final.toolActivity || m.tools || []).map(t => ({
              ...t,
              done: true
            })),
            streaming: false
          }));
          if (data.conversationId && data.conversationId !== conversationId) {
            skipReloadRef.current = true;
            setConversationId(data.conversationId);
            onConversationChange?.(data.conversationId);
          }
        } else if (event === 'error') {
          const e = new Error(data.message || 'The assistant hit a problem.');
          e.kind = data.kind || 'transient';
          throw e;
        }
      }
    });
    try {
      try {
        await attempt();
      } catch (e) {
        // Auth expiry: one silent retry through the supplier (Q5-4) — but
        // only if nothing streamed yet (a retry would double the turn).
        if (e.kind === 'auth' && !received && !controller.signal.aborted) {
          await attempt();
        } else throw e;
      }
      patch(m => m.streaming ? {
        ...m,
        streaming: false
      } : m);
      setPaused(null);
    } catch (e) {
      if (controller.signal.aborted) {
        // Stop pressed: keep what rendered, marked "stopped" (§3).
        patch(m => ({
          ...m,
          streaming: false,
          status: 'stopped'
        }));
      } else {
        if (e.kind === 'credits') {
          // §53b: not an error — the meter paused the workspace. Calm
          // notice, composer disabled; "Check again" re-enables (the
          // server re-refuses if credits are still out).
          setMessages(prev => prev.filter(m => m.id !== pendingId));
          setPaused(e.message || 'This workspace is out of generation credits, so chat is paused. It picks right back up when credits are added.');
          return;
        }
        // Inline error with retry — plain language, never a raw exception.
        const msg = e.kind === 'auth' ? 'Your session expired — sign in again, then retry.' : e.message === 'Failed to fetch' ? "I couldn't reach the assistant — check your connection and retry." : e.message;
        setMessages(prev => prev.filter(m => m.id !== pendingId));
        setFatal({
          message: msg,
          retry: message
        });
      }
    } finally {
      clearTimeout(slowTimer.current);
      abortRef.current = null;
      setStreaming(false);
    }
  }, [apiBase, authHeaders, context, conversationId, input, onConversationChange, streaming]);
  const stop = () => abortRef.current?.abort();
  const openList = async () => {
    setView('list');
    setConvos(null);
    try {
      setConvos(await api('/v1/chat/conversations'));
    } catch {
      setConvos([]);
    }
  };
  const openConvo = id => {
    setView('chat');
    if (id !== conversationId) {
      setConversationId(id);
      onConversationChange?.(id);
    }
  };
  const newChat = () => {
    if (streaming) stop();
    setView('chat');
    setConversationId(null);
    onConversationChange?.(null);
    setMessages([]);
    setFatal(null);
    inputRef.current?.focus();
  };
  const deleteConvo = async id => {
    try {
      await api(`/v1/chat/conversations/${id}`, {
        method: 'DELETE'
      });
      setConvos(prev => (prev || []).filter(c => c.id !== id));
      if (id === conversationId) {
        setConversationId(null);
        onConversationChange?.(null);
        setMessages([]);
      }
    } catch {/* transient — list refreshes next open */}
  };
  const onKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };
  const empty = view === 'chat' && messages != null && messages.length === 0 && !fatal;
  return /*#__PURE__*/React.createElement("div", {
    className: `pchat pchat--${theme}`
  }, /*#__PURE__*/React.createElement("div", {
    className: "pchat-toolbar"
  }, /*#__PURE__*/React.createElement("button", {
    className: "pchat-toolbtn",
    onClick: view === 'list' ? () => setView('chat') : openList
  }, view === 'list' ? '‹ back' : 'history'), /*#__PURE__*/React.createElement("button", {
    className: "pchat-toolbtn",
    onClick: newChat
  }, "new chat")), view === 'list' && /*#__PURE__*/React.createElement("div", {
    className: "pchat-list-view"
  }, convos == null && /*#__PURE__*/React.createElement("p", {
    style: {
      color: 'var(--pchat-dim)',
      fontSize: '12.5px'
    }
  }, "Loading\u2026"), convos != null && convos.length === 0 && /*#__PURE__*/React.createElement("p", {
    style: {
      color: 'var(--pchat-dim)',
      fontSize: '12.5px'
    }
  }, "No conversations yet."), (convos || []).map(c => /*#__PURE__*/React.createElement("div", {
    key: c.id,
    className: "pchat-convo"
  }, /*#__PURE__*/React.createElement("button", {
    className: "pchat-convo-main",
    onClick: () => openConvo(c.id)
  }, /*#__PURE__*/React.createElement("div", {
    className: "pchat-convo-title"
  }, c.title || 'Conversation'), /*#__PURE__*/React.createElement("div", {
    className: "pchat-convo-date"
  }, fmtDate(c.updatedDate))), /*#__PURE__*/React.createElement("button", {
    className: "pchat-convo-del",
    onClick: () => deleteConvo(c.id),
    title: "Delete conversation"
  }, "\u2715"))), convos != null && /*#__PURE__*/React.createElement("p", {
    className: "pchat-retention"
  }, "Conversations are kept for 90 days.")), view === 'chat' && empty && /*#__PURE__*/React.createElement("div", {
    className: "pchat-empty"
  }, /*#__PURE__*/React.createElement("h3", null, welcomeTitle), welcomeSub && /*#__PURE__*/React.createElement("p", null, welcomeSub), suggestions.map(s => /*#__PURE__*/React.createElement("button", {
    key: s,
    className: "pchat-suggestion",
    onClick: () => send(s)
  }, s))), view === 'chat' && !empty && /*#__PURE__*/React.createElement("div", {
    className: "pchat-thread",
    ref: threadRef,
    onScroll: onThreadScroll,
    "aria-live": "polite"
  }, messages == null && /*#__PURE__*/React.createElement("p", {
    style: {
      color: 'var(--pchat-dim)',
      fontSize: '12.5px'
    }
  }, "Loading conversation\u2026"), (messages || []).map(m => m.role === 'user' ? /*#__PURE__*/React.createElement("div", {
    key: m.id,
    className: "pchat-row pchat-row--user"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pchat-bubble pchat-bubble--user"
  }, m.content)) : /*#__PURE__*/React.createElement(AssistantBubble, {
    key: m.id,
    msg: m
  })), fatal && /*#__PURE__*/React.createElement("div", {
    className: "pchat-row"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pchat-error"
  }, fatal.message, /*#__PURE__*/React.createElement("button", {
    onClick: () => send(fatal.retry)
  }, "Try again"))), showJump && /*#__PURE__*/React.createElement("button", {
    className: "pchat-jump",
    onClick: () => {
      atBottomRef.current = true;
      scrollToBottom();
      setShowJump(false);
    }
  }, "\u2193 Jump to latest")), view === 'chat' && paused && /*#__PURE__*/React.createElement("div", {
    className: "pchat-paused"
  }, /*#__PURE__*/React.createElement("span", null, paused), /*#__PURE__*/React.createElement("button", {
    onClick: () => {
      setPaused(null);
      inputRef.current?.focus();
    }
  }, "Check again")), view === 'chat' && /*#__PURE__*/React.createElement("div", {
    className: "pchat-replybar"
  }, /*#__PURE__*/React.createElement("textarea", {
    ref: inputRef,
    className: "pchat-input",
    rows: 1,
    value: input,
    placeholder: placeholder,
    onChange: e => setInput(e.target.value),
    onKeyDown: onKeyDown,
    disabled: streaming || !!paused
  }), streaming ? /*#__PURE__*/React.createElement("button", {
    className: "pchat-send pchat-send--stop",
    onClick: stop,
    title: "Stop"
  }, "\u25A0") : /*#__PURE__*/React.createElement("button", {
    className: "pchat-send",
    onClick: () => send(),
    disabled: !input.trim() || !!paused,
    title: "Send"
  }, "\u27A4")));
}